/**
 * Files Navigation Component
 * Left sidebar with folder tree and special folders
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    Folder,
    FolderOpen,
    ChevronRight,
    FileStack,
    User,
    Clock,
    Library,
} from 'lucide-react';
import { api, type ApiListResponse } from '@/lib/api';

interface FolderItem {
    id: string;
    name: string;
    parent: string | null;
}

interface NestedFolder extends FolderItem {
    children: NestedFolder[];
}

interface FilesNavigationProps {
    currentFolder?: string;
    currentSpecial?: 'all' | 'mine' | 'recent';
    refreshTrigger?: number; // Timestamp to trigger folder refetch
}

// Build nested folder structure
function buildNestedFolders(folders: FolderItem[], parentId: string | null = null): NestedFolder[] {
    return folders
        .filter(f => f.parent === parentId)
        .map(folder => ({
            ...folder,
            children: buildNestedFolders(folders, folder.id),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

// Recursive folder item component
function FolderTreeItem({
    folder,
    currentFolder,
    depth = 0,
    openFolders,
    toggleFolder,
    onNavigate,
}: {
    folder: NestedFolder;
    currentFolder?: string;
    depth?: number;
    openFolders: Set<string>;
    toggleFolder: (id: string) => void;
    onNavigate: (folderId: string) => void;
}) {
    const isOpen = openFolders.has(folder.id);
    const isActive = currentFolder === folder.id;
    const hasChildren = folder.children.length > 0;

    return (
        <div>
            <div
                className={cn(
                    'flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer hover:bg-accent transition-colors',
                    isActive && 'bg-accent text-accent-foreground font-medium',
                )}
                style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
                {hasChildren ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(folder.id);
                        }}
                    >
                        <ChevronRight
                            className={cn(
                                'h-3.5 w-3.5 transition-transform',
                                isOpen && 'rotate-90'
                            )}
                        />
                    </Button>
                ) : (
                    <div className="w-5" />
                )}
                <div
                    className="flex items-center gap-2 flex-1 min-w-0"
                    onClick={() => onNavigate(folder.id)}
                >
                    {isOpen ? (
                        <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                        <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="truncate text-sm">{folder.name}</span>
                </div>
            </div>
            {hasChildren && isOpen && (
                <div>
                    {folder.children.map(child => (
                        <FolderTreeItem
                            key={child.id}
                            folder={child}
                            currentFolder={currentFolder}
                            depth={depth + 1}
                            openFolders={openFolders}
                            toggleFolder={toggleFolder}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function FilesNavigation({ currentFolder, currentSpecial, refreshTrigger }: FilesNavigationProps) {
    const navigate = useNavigate();
    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [openFolders, setOpenFolders] = useState<Set<string>>(new Set(['root']));

    // Fetch all folders
    useEffect(() => {
        const fetchFolders = async () => {
            try {
                const response = await api.get<ApiListResponse<FolderItem>>('/folders?limit=-1&sort=name');
                setFolders(response.data || []);
            } catch (error) {
                console.error('Error fetching folders:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFolders();
    }, [refreshTrigger]); // Re-fetch when refreshTrigger changes

    // Build nested folder structure
    const nestedFolders = useMemo(() => buildNestedFolders(folders), [folders]);

    // Auto-expand folders to show current folder
    useEffect(() => {
        if (currentFolder && folders.length > 0) {
            const foldersToOpen = new Set(openFolders);
            let current = folders.find(f => f.id === currentFolder);

            while (current?.parent) {
                foldersToOpen.add(current.parent);
                current = folders.find(f => f.id === current?.parent);
            }
            foldersToOpen.add('root');

            setOpenFolders(foldersToOpen);
        }
    }, [currentFolder, folders]);

    const toggleFolder = (id: string) => {
        setOpenFolders(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleNavigate = (target: { folder?: string; special?: string }) => {
        if (target.folder) {
            navigate(`/admin/files/folders/${target.folder}`);
        } else if (target.special) {
            navigate(`/admin/files/${target.special}`);
        } else {
            navigate('/admin/files');
        }
    };

    const isRootActive = !currentFolder && !currentSpecial;

    return (
        <nav className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto py-2">
                {/* File Library (Root) */}
                <div className="px-2">
                    <Collapsible
                        open={openFolders.has('root')}
                        onOpenChange={() => toggleFolder('root')}
                    >
                        <div
                            className={cn(
                                'flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer hover:bg-accent transition-colors',
                                isRootActive && 'bg-accent text-accent-foreground font-medium',
                            )}
                        >
                            <CollapsibleTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ChevronRight
                                        className={cn(
                                            'h-3.5 w-3.5 transition-transform',
                                            openFolders.has('root') && 'rotate-90'
                                        )}
                                    />
                                </Button>
                            </CollapsibleTrigger>
                            <div
                                className="flex items-center gap-2 flex-1"
                                onClick={() => handleNavigate({})}
                            >
                                <Library className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">File Library</span>
                            </div>
                        </div>

                        <CollapsibleContent>
                            {loading ? (
                                <div className="py-2 px-4">
                                    <div className="h-4 bg-muted rounded animate-pulse" />
                                </div>
                            ) : nestedFolders.length > 0 ? (
                                nestedFolders.map(folder => (
                                    <FolderTreeItem
                                        key={folder.id}
                                        folder={folder}
                                        currentFolder={currentFolder}
                                        depth={1}
                                        openFolders={openFolders}
                                        toggleFolder={toggleFolder}
                                        onNavigate={(id) => handleNavigate({ folder: id })}
                                    />
                                ))
                            ) : null}
                        </CollapsibleContent>
                    </Collapsible>
                </div>

                {/* Divider */}
                <div className="my-2 mx-2 border-t border-border" />

                {/* Special folders */}
                <div className="px-2 space-y-0.5">
                    <button
                        onClick={() => handleNavigate({ special: 'all' })}
                        className={cn(
                            'w-full flex items-center gap-2 py-1.5 px-2 rounded-md text-sm hover:bg-accent transition-colors',
                            currentSpecial === 'all' && 'bg-accent text-accent-foreground font-medium',
                        )}
                    >
                        <FileStack className="h-4 w-4 text-muted-foreground" />
                        <span>All Files</span>
                    </button>

                    <button
                        onClick={() => handleNavigate({ special: 'mine' })}
                        className={cn(
                            'w-full flex items-center gap-2 py-1.5 px-2 rounded-md text-sm hover:bg-accent transition-colors',
                            currentSpecial === 'mine' && 'bg-accent text-accent-foreground font-medium',
                        )}
                    >
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>My Files</span>
                    </button>

                    <button
                        onClick={() => handleNavigate({ special: 'recent' })}
                        className={cn(
                            'w-full flex items-center gap-2 py-1.5 px-2 rounded-md text-sm hover:bg-accent transition-colors',
                            currentSpecial === 'recent' && 'bg-accent text-accent-foreground font-medium',
                        )}
                    >
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>Recent Files</span>
                    </button>
                </div>
            </div>
        </nav>
    );
}
